import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import SignaturePad from "components/signature-pad";

const PAD_HEIGHT = 200;

/**
 * Handwritten signature capture. Typing a name is deliberately not offered:
 * this document exists to defend chargebacks, and a drawn mark is what an
 * employee can hold against the signature in the payer's passport.
 *
 * Exposes `getSignature()` which resolves to `{ dataUrl, method }`, or null
 * when the pad is still empty.
 */
const SignatureCapture = forwardRef(({ t, disabled, onValidityChange }, ref) => {
  const padRef = useRef(null);
  const [hasDrawing, setHasDrawing] = useState(false);

  const handleChange = (drawn) => {
    setHasDrawing(drawn);
    onValidityChange?.(drawn);
  };

  useImperativeHandle(ref, () => ({
    getSignature: async () => {
      if (!padRef.current || padRef.current.isEmpty()) return null;
      return { dataUrl: padRef.current.toDataURL(), method: "DRAWN" };
    },
  }));

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 1.5, fontSize: 13 }}
      >
        {t("signDrawHint")}
      </Typography>

      <SignaturePad
        ref={padRef}
        height={PAD_HEIGHT}
        disabled={disabled}
        onChange={handleChange}
      />

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          color="inherit"
          disabled={disabled || !hasDrawing}
          onClick={() => padRef.current?.undo()}
        >
          {t("signUndo")}
        </Button>
        <Button
          size="small"
          color="inherit"
          disabled={disabled || !hasDrawing}
          onClick={() => padRef.current?.clear()}
        >
          {t("signClear")}
        </Button>
      </Stack>
    </Box>
  );
});

SignatureCapture.displayName = "SignatureCapture";

export default SignatureCapture;
